import React, { type Key, type ReactNode } from 'react';
import ChevronDown from '@uber/base-icons/chevron-down';
import ChevronUp from '@uber/base-icons/chevron-up';
import { useStyletron } from 'baseui';
import type { StyleObject } from 'styletron-react';
export type SidePanelAccordionSectionT = {
  id: Key;
  header: ReactNode;
  children: ReactNode;
  endEnhancer?: ReactNode;
  pushToBottom?: boolean;
};
type SidePanelAccordionOverridesT = {
  Root?: StyleObject;
  Section?: StyleObject;
  Header?: StyleObject;
  ToggleButton?: StyleObject;
  Content?: StyleObject;
};
type SidePanelAccordionPropsT = {
  sections: SidePanelAccordionSectionT[];
  expandedKeys: Key[];
  onChange: (expandedKeys: Key[]) => void;
  accordion?: boolean;
  fillExpandedSection?: boolean;
  fillContainer?: boolean;
  overrides?: SidePanelAccordionOverridesT;
};
const AccordionRoot = ({
  children,
  fillContainer,
  overrides
}: {
  children: ReactNode;
  fillContainer: boolean;
  overrides?: SidePanelAccordionOverridesT;
}) => {
  const [css] = useStyletron();
  return <div data-testid="side-panel-accordion" className={css({
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    ...(fillContainer ? {
      height: '100%',
      minHeight: 0,
      overflow: 'hidden'
    } : {}),
    ...overrides?.Root
  })}>
      {children}
    </div>;
};
const AccordionHeader = ({
  section,
  isExpanded,
  onToggle,
  overrides
}: {
  section: SidePanelAccordionSectionT;
  isExpanded: boolean;
  onToggle: () => void;
  overrides?: SidePanelAccordionOverridesT;
}) => {
  const [css, theme] = useStyletron();
  return <div className={css({
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.contentPrimary,
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    boxSizing: 'border-box',
    gap: theme.sizing.scale300,
    justifyContent: 'flex-start',
    margin: 0,
    minWidth: 0,
    padding: theme.sizing.scale600,
    textAlign: 'left',
    width: '100%',
    ...overrides?.Header
  })}>
      <button type="button" aria-expanded={isExpanded} onClick={onToggle} className={css({
      alignItems: 'center',
      backgroundColor: 'transparent',
      border: 'none',
      color: theme.colors.contentPrimary,
      cursor: 'pointer',
      display: 'flex',
      flex: 1,
      justifyContent: 'flex-start',
      margin: 0,
      minWidth: 0,
      padding: 0,
      textAlign: 'left',
      ...overrides?.ToggleButton
    })}>
        {section.header}
      </button>
      {section.endEnhancer}
      <button type="button" aria-label={`Toggle ${String(section.id)}`} aria-expanded={isExpanded} onClick={onToggle} className={css({
      alignItems: 'center',
      backgroundColor: 'transparent',
      border: 'none',
      color: theme.colors.contentPrimary,
      cursor: 'pointer',
      display: 'flex',
      flexShrink: 0,
      justifyContent: 'center',
      margin: 0,
      padding: 0
    })}>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
    </div>;
};
const AccordionContent = ({
  children,
  fillExpandedSection,
  overrides
}: {
  children: ReactNode;
  fillExpandedSection: boolean;
  overrides?: SidePanelAccordionOverridesT;
}) => {
  const [css, theme] = useStyletron();
  return <div className={css({
    boxSizing: 'border-box',
    ...(fillExpandedSection ? {
      flex: 1
    } : {}),
    minHeight: 0,
    minWidth: 0,
    overflowY: 'auto',
    padding: `0px ${theme.sizing.scale600}`,
    width: '100%',
    ...overrides?.Content
  })}>
      {children}
    </div>;
};
const AccordionSection = ({
  section,
  index,
  isExpanded,
  fillExpandedSection,
  onToggle,
  overrides
}: {
  section: SidePanelAccordionSectionT;
  index: number;
  isExpanded: boolean;
  fillExpandedSection: boolean;
  onToggle: () => void;
  overrides?: SidePanelAccordionOverridesT;
}) => {
  const [css, theme] = useStyletron();
  return <section key={String(section.id)} className={css({
    borderBottom: 'none',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    boxSizing: 'border-box',
    minHeight: 0,
    minWidth: 0,
    position: 'relative',
    width: '100%',
    ...(isExpanded && fillExpandedSection ? {
      flex: 1
    } : {}),
    ...(section.pushToBottom ? {
      marginTop: 'auto'
    } : {}),
    ...(index > 0 ? {
      ':before': {
        borderTop: `1px solid ${theme.colors.borderOpaque}`,
        content: '""',
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0
      }
    } : {}),
    ...overrides?.Section
  })}>
      <AccordionHeader section={section} isExpanded={isExpanded} onToggle={onToggle} overrides={overrides} />
      {isExpanded && <AccordionContent fillExpandedSection={fillExpandedSection} overrides={overrides}>
          {section.children}
        </AccordionContent>}
    </section>;
};
export const SidePanelAccordion = ({
  sections,
  expandedKeys,
  onChange,
  accordion = false,
  fillExpandedSection = true,
  fillContainer = true,
  overrides
}: SidePanelAccordionPropsT) => {
  const toggleSection = (sectionId: Key) => {
    if (expandedKeys.includes(sectionId)) {
      onChange(expandedKeys.filter(expandedKey => expandedKey !== sectionId));
      return;
    }
    onChange(accordion ? [sectionId] : [...expandedKeys, sectionId]);
  };
  return <AccordionRoot fillContainer={fillContainer} overrides={overrides}>
      {sections.map((section, index) => <AccordionSection key={String(section.id)} section={section} index={index} isExpanded={expandedKeys.includes(section.id)} fillExpandedSection={fillExpandedSection} onToggle={() => toggleSection(section.id)} overrides={overrides} />)}
    </AccordionRoot>;
};